import { useServices } from "@hq/client-react";
import type { Task } from "@hq/contracts";
import { orderChanges, type TaskGroup } from "@hq/core";
import { DragDropRoot, moveItems, restoreDragFocus, useDroppable, useSortable } from "@z/dnd";
import { cn, toast } from "@z/ui";
import { GripVertical } from "lucide-react";
import { useRef, useState, type ReactNode } from "react";

type GroupItems = Record<string, Task[]>;
export type MoveToGroup = (task: Task, group: TaskGroup) => Promise<void>;
export type CanMoveToGroup = (task: Task, group: TaskGroup) => boolean;

/** One drag context for the entire list, including empty destination groups. */
export function TaskOrder({
  groups,
  onMoveToGroup,
  canMoveToGroup,
  children,
}: {
  groups: readonly TaskGroup[];
  onMoveToGroup?: MoveToGroup | undefined;
  canMoveToGroup?: CanMoveToGroup | undefined;
  children: (
    groups: readonly TaskGroup[],
    state: { dragging: boolean; saving: boolean; canDrop: (group: TaskGroup) => boolean },
  ) => ReactNode;
}) {
  const services = useServices();
  const [preview, setPreview] = useState<GroupItems | null>(null);
  const [saving, setSaving] = useState(false);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const items: GroupItems = Object.fromEntries(groups.map((group) => [group.key, group.tasks]));
  const [announcement, setAnnouncement] = useState("");
  return (
    <DragDropRoot
      onBeforeDragStart={(event) => {
        if (saving) event.preventDefault();
      }}
      onDragStart={({ operation }) => {
        setDraggedId(String(operation.source?.id));
        setPreview(items);
      }}
      onDragOver={(event) => {
        event.preventDefault();
        const sourceId = String(event.operation.source?.id);
        const targetId = String(event.operation.target?.id);
        const source = groups.find((group) =>
          group.tasks.some((candidate) => candidate.id === sourceId),
        );
        const task = source?.tasks.find((candidate) => candidate.id === sourceId);
        const current = preview ?? items;
        const target = groups.find(
          (group) =>
            group.key === targetId ||
            current[group.key]?.some((candidate) => candidate.id === targetId),
        );
        if (
          !task ||
          !target ||
          (source?.key !== target.key &&
            (!onMoveToGroup || canMoveToGroup?.(task, target) === false))
        ) {
          setPreview(items);
          return;
        }
        setPreview(moveItems(current, event));
      }}
      onDragEnd={async (event) => {
        const id = String(event.operation.source?.id);
        try {
          if (event.canceled || !event.operation.target || !preview) return;
          const source = groups.find((group) =>
            group.tasks.some((candidate) => candidate.id === id),
          );
          const destination = groups.find((group) =>
            preview[group.key]?.some((candidate) => candidate.id === id),
          );
          const task = source?.tasks.find((candidate) => candidate.id === id);
          if (!source || !destination || !task) return;
          const ordered = preview[destination.key] ?? [];
          if (
            source.key === destination.key &&
            ordered.every((candidate, index) => candidate.id === destination.tasks[index]?.id)
          )
            return;
          setSaving(true);
          if (source.key !== destination.key) {
            if (!onMoveToGroup || canMoveToGroup?.(task, destination) === false) return;
            await onMoveToGroup(task, destination);
          }
          for (const change of orderChanges(ordered))
            await services.tasks.reorder(change.id, change.sortOrder);
          setAnnouncement(
            `Moved ${task.title}${destination.label ? ` to ${destination.label}` : ""}.`,
          );
        } catch {
          toast({
            title: "Could not move task",
            description: "Please try again.",
            variant: "danger",
          });
        } finally {
          setSaving(false);
          setPreview(null);
          setDraggedId(null);
          if (event.nativeEvent instanceof KeyboardEvent) restoreDragFocus(rootRef.current, id);
        }
      }}
    >
      <div ref={rootRef} tabIndex={-1}>
        <div role="status" className="sr-only">
          {announcement}
        </div>
        {children(
          groups.map((group) => ({ ...group, tasks: preview?.[group.key] ?? group.tasks })),
          {
            dragging: preview !== null,
            saving,
            canDrop: (group) => {
              // The container around the dragged row must not capture arrow-key
              // movement before the next group. Its other rows remain sortable.
              if (group.tasks.some((candidate) => candidate.id === draggedId)) return false;
              const source = groups.find((g) =>
                g.tasks.some((candidate) => candidate.id === draggedId),
              );
              const task = source?.tasks.find((candidate) => candidate.id === draggedId);
              return (
                !task ||
                source?.key === group.key ||
                (!!onMoveToGroup && canMoveToGroup?.(task, group) !== false)
              );
            },
          },
        )}
      </div>
    </DragDropRoot>
  );
}

export function TaskDropGroup({
  group,
  enabled,
  acceptDrop,
  children,
}: {
  group: TaskGroup;
  enabled: boolean;
  acceptDrop: boolean;
  children: ReactNode;
}) {
  return enabled ? (
    <DroppableTaskGroup group={group} acceptDrop={acceptDrop}>
      {children}
    </DroppableTaskGroup>
  ) : (
    <div>{children}</div>
  );
}

function DroppableTaskGroup({
  group,
  acceptDrop,
  children,
}: {
  group: TaskGroup;
  acceptDrop: boolean;
  children: ReactNode;
}) {
  const { ref, isDropTarget } = useDroppable({
    id: group.key,
    accept: "task-order",
    disabled: !acceptDrop,
    collisionPriority: -1,
    data: { label: group.label || "Tasks" },
  });
  return (
    <div
      ref={ref}
      className={cn(
        "rounded-md transition-colors",
        isDropTarget && "bg-accent-muted/40 ring-1 ring-inset ring-accent/30",
      )}
    >
      {children}
    </div>
  );
}

export function SortableTask({
  task,
  index,
  group,
  disabled,
  children,
}: {
  task: Task;
  index: number;
  group: string;
  disabled: boolean;
  children: ReactNode;
}) {
  const { ref, handleRef, isDragSource } = useSortable({
    id: task.id,
    index,
    group,
    type: "task-order",
    accept: "task-order",
    disabled,
    data: { label: task.title },
  });
  return (
    <div
      ref={ref}
      data-task-id={task.id}
      className={cn(
        "group/task relative flex items-start",
        isDragSource && "z-20 rounded-md bg-surface-raised shadow-lg",
      )}
    >
      <button
        ref={handleRef}
        type="button"
        aria-label={`Reorder ${task.title}`}
        title="Drag to move · Space, arrow keys, Space"
        data-drag-handle={task.id}
        className="mt-2 group-has-[[data-density=compact]]/task:mt-1 flex size-7 shrink-0 touch-none cursor-grab items-center justify-center rounded-sm text-fg-muted opacity-40 hover:bg-fg/6 hover:text-fg focus:opacity-100 group-hover/task:opacity-100 focus-ring"
      >
        <GripVertical className="size-3.5" />
      </button>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}
